import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

function App() {
    const [totalMl, setTotalMl] = useState(0);
    const [showButtons, setShowButtons] = useState(false);
    const [trivia, setTrivia] = useState([]);
    const [currentTrivia, setCurrentTrivia] = useState('');
    const [history, setHistory] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingAmount, setPendingAmount] = useState(0);
    const [isShaking, setIsShaking] = useState(false);
    const [currentSection, setCurrentSection] = useState(0);
    const mainSectionRef = useRef(null);
    const graphSectionRef = useRef(null);

    // Load data from localStorage and trivia on mount
    useEffect(() => {
        const savedTotal = localStorage.getItem('cumliters_total');
        const savedHistory = localStorage.getItem('cumliters_history');

        if (savedTotal) {
            setTotalMl(parseFloat(savedTotal));
        }

        if (savedHistory) {
            try {
                setHistory(JSON.parse(savedHistory));
            } catch (error) {
                console.error('Error parsing saved history:', error);
            }
        }

        // Load trivia data
        fetch('/trivia.json')
            .then(response => response.json())
            .then(data => {
                setTrivia(data);
            })
            .catch(error => console.error('Error loading trivia:', error));
    }, []);

    // Full page scroll functionality
    useEffect(() => {
        let isScrolling = false;

        const handleWheel = (e) => {
            if (isScrolling || showConfirm) return;

            e.preventDefault();
            isScrolling = true;

            if (e.deltaY > 0 && currentSection === 0) {
                // Scroll down to graph section
                setCurrentSection(1);
                if (graphSectionRef.current) {
                    graphSectionRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            } else if (e.deltaY < 0 && currentSection === 1) {
                // Scroll up to main section
                setCurrentSection(0);
                if (mainSectionRef.current) {
                    mainSectionRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }

            setTimeout(() => {
                isScrolling = false;
            }, 1000);
        };

        window.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            window.removeEventListener('wheel', handleWheel);
        };
    }, [currentSection, showConfirm]);

    // Update trivia based on total ml
    useEffect(() => {
        if (trivia.length > 0) {
            const relevantTrivia = trivia.find(item =>
                totalMl >= item.minMl && totalMl < item.maxMl
            );
            if (relevantTrivia) {
                setCurrentTrivia(`${totalMl.toFixed(1)}ml - ${relevantTrivia.fact}`);
            }
        }
    }, [totalMl, trivia]);

    // Save to localStorage whenever totalMl or history changes
    useEffect(() => {
        localStorage.setItem('cumliters_total', totalMl.toString());
    }, [totalMl]);

    useEffect(() => {
        localStorage.setItem('cumliters_history', JSON.stringify(history));
    }, [history]);

    // Play random sound effect
    const playRandomSound = () => {
        const soundFiles = ['fx_01.ogg', 'fx_02.ogg', 'fx_03.ogg', 'fx_04.ogg', 'fx_05.ogg', 'fx_06.ogg', 'fx_07.ogg'];
        const randomSound = soundFiles[Math.floor(Math.random() * soundFiles.length)];
        const audio = new Audio(`/fx/${randomSound}`);
        audio.volume = 0.6;
        audio.play().catch(error => console.log('Audio play failed:', error));
    };

    // Trigger screen shake animation
    const triggerShake = () => {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 1000);
    };

    const handleCumButtonClick = () => {
        if (!showConfirm) { // Only show buttons if popup is not open
            setShowButtons(true);
            setTimeout(() => {
                setShowButtons(false);
            }, 5000); // Hide buttons after 5 seconds
        }
    };

    const handleCameNormally = () => {
        const amount = Math.random() * (3 - 2.5) + 2.5; // Random between 2.5-3ml
        setPendingAmount(amount);
        setShowConfirm(true);
        setShowButtons(false);
    };

    const handleCameALot = () => {
        const amount = Math.random() * (8 - 5) + 5; // Random between 5-8ml
        setPendingAmount(amount);
        setShowConfirm(true);
        setShowButtons(false);
    };

    const confirmAdd = () => {
        // Play sound and trigger shake animation
        playRandomSound();
        triggerShake();

        const newTotal = totalMl + pendingAmount;
        setTotalMl(newTotal);

        // Add to history
        const newEntry = {
            date: new Date().toISOString().split('T')[0],
            amount: pendingAmount,
            total: newTotal
        };
        setHistory(prev => [...prev, newEntry]);

        setShowConfirm(false);
        setPendingAmount(0);
        setShowButtons(false); // Ensure buttons are hidden
    };

    const cancelAdd = () => {
        setShowConfirm(false);
        setPendingAmount(0);
        setShowButtons(false); // Ensure buttons are hidden
    };

    // Scroll to graph section
    const scrollToGraph = () => {
        setCurrentSection(1);
        if (graphSectionRef.current) {
            graphSectionRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Prepare chart data
    const chartData = {
        labels: history.map(entry => entry.date),
        datasets: [{
            label: 'Daily Amount (ml)',
            data: history.map(entry => entry.amount),
            borderColor: '#ff69b4',
            backgroundColor: 'rgba(255, 105, 180, 0.1)',
            borderWidth: 3,
            pointBackgroundColor: '#ff69b4',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 6,
            tension: 0.4,
            fill: true,
        }, ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: '#ff69b4',
                    font: {
                        size: 16,
                        weight: 'bold'
                    }
                }
            },
            title: {
                display: true,
                text: 'Your Journey',
                color: '#ff69b4',
                font: {
                    size: 24,
                    weight: 'bold'
                }
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    color: '#ff69b4',
                },
                grid: {
                    color: 'rgba(255, 105, 180, 0.2)',
                }
            },
            x: {
                ticks: {
                    color: '#ff69b4',
                },
                grid: {
                    color: 'rgba(255, 105, 180, 0.2)',
                }
            }
        },
    };

    return ( <
            div className = { `App ${isShaking ? 'shake-animation' : ''}` } > { /* Main Section */ } <
            section ref = { mainSectionRef }
            className = "main-section" >
            <
            div className = "main-content" >
            <
            h1 className = "app-title" > Cumliters < /h1> <
            p className = "app-subtitle" > Cum tracking app that respects your privacy < /p> <
            button className = "cum-button"
            onClick = { handleCumButtonClick } >
            CUM <
            /button> <
            div className = "total-display" >
            <
            span className = "total-number" > { totalMl.toFixed(1) } < /span> <
            span className = "ml-unit" > ml < /span> < /
            div > <
            p className = "trivia-text" > { currentTrivia } < /p> < /
            div >

            { /* Action Buttons */ } {
                showButtons && !showConfirm && ( <
                    div className = "action-buttons-overlay" >
                    <
                    div className = "action-buttons" >
                    <
                    button className = "action-btn normal-btn"
                    onClick = { handleCameNormally } >
                    I came normally <
                    span className = "btn-subtitle" > (+2.5 - 3 ml) < /span> < /
                    button > <
                    button className = "action-btn lot-btn"
                    onClick = { handleCameALot } >
                    I came a lot!
                    <
                    span className = "btn-subtitle" > (+5 - 8 ml) < /span> < /
                    button > <
                    /div> < /
                    div >
                )
            } <
            /section>

            { /* Graph Section */ } <
            section ref = { graphSectionRef }
            className = "graph-section" >
            <
            div className = "graph-content" >
            <
            h2 className = "graph-title" > Your History < /h2> {
            history.length > 0 ? ( <
                div className = "chart-container" >
                <
                Line data = { chartData }
                options = { chartOptions }
                /> < /
                div >
            ) : ( <
                div className = "no-data" >
                <
                p > No data yet!Start tracking to see your progress. < /p> < /
                div >
            )
        } <
        /div> < /
    section >

        { /* Scroll Indicators */ } {
            currentSection === 0 && ( <
                div className = "scroll-indicator"
                onClick = { scrollToGraph } >
                Scroll
                for history↓ <
                /div>
            )
        }

    {
        currentSection === 1 && ( <
            div className = "scroll-up"
            onClick = {
                () => {
                    setCurrentSection(0);
                    if (mainSectionRef.current) {
                        mainSectionRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }
            } > ↑Back to main <
            /div>
        )
    }

    { /* Confirmation Dialog */ } {
        showConfirm && ( <
            div className = "confirm-overlay-blur" >
            <
            div className = "confirm-dialog"
            onClick = {
                (e) => e.stopPropagation()
            } >
            <
            h3 > Did you really came ? < /h3> <
            p > Adding { pendingAmount.toFixed(1) }
            ml to your total < /p> <
            div className = "confirm-buttons" >
            <
            button className = "confirm-btn yes-btn"
            onClick = { confirmAdd } > Yes < /button> <
            button className = "confirm-btn no-btn"
            onClick = { cancelAdd } > No < /button> < /
            div > <
            /div> < /
            div >
        )
    } <
    /div>
);
}

export default App;